import React, { useState, useCallback, useMemo, forwardRef, useEffect } from 'react'
import { useDispatch } from 'react-redux'

import PropTypes from 'prop-types'

import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import toLower from 'lodash/toLower'

import Grid from '@material-ui/core/Grid'

import { reloadSchema } from '@galo-monitor/forms'
import userSchema from '@galo-monitor/forms/schemas/company/user.schema'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { CompanyUserActions } from '@galo-monitor/stores/companyUser'
import Form from '@galo-monitor/web-components/Form'
import InputEmail from '@galo-monitor/web-components/InputEmail'
import InputPhone from '@galo-monitor/web-components/InputPhone'
import InputSelect from '@galo-monitor/web-components/InputSelect'
import InputText from '@galo-monitor/web-components/InputText'
import RadioGroup from '@galo-monitor/web-components/RadioGroup'

import { Container } from './styles'

const UserForm = forwardRef((props, formRef) => {
  const {
    user,
    loading,
    setLoading,
    onSuccess,
    onError,
    userDataEndpoint,
    farmsEndpoint,
    rolesEndpoint
  } = props
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()

  const { data: userData, isValidating: isValidatingUserData } = useGaloMonitorApi(userDataEndpoint)
  const { data: farms, isValidating: isValidatingFarms } = useGaloMonitorApi(farmsEndpoint)
  const { data: roles, isValidating: isValidatingRoles } = useGaloMonitorApi(rolesEndpoint)

  const [disableFarms, setDisableFarms] = useState(true)

  const farmOptions = useMemo(
    () => map(farms, farm => ({
      label: farm.name,
      value: farm.id.toString()
    })),
    [farms]
  )

  const rolesOptions = useMemo(
    () => map(roles, profile => ({
      label: profile.name,
      value: profile.id.toString()
    })),
    [roles]
  )

  const userFarmsDefaultValue = useMemo(
    () => !isEmpty(userData)
      ? map(userData.farms, farm => farm.id.toString())
      : [],
    [userData]
  )

  const modeOptions = useMemo(
    () => [
      { label: t('no access'), value: 'none' },
      { label: t('all farms'), value: 'all' },
      { label: t('selected farms'), value: 'custom' }
    ],
    [t]
  )

  const modeDefaultValue = useMemo(
    () => {
      let mode = 'all'

      if (!isEmpty(userData)) {
        if (userData.farmsAlways)
          return mode

        const userHasFarms = !isEmpty(userData.farms)

        mode = userHasFarms ? 'custom' : 'none'
      }

      return mode
    },
    [userData]
  )

  const revalidateSchema = useCallback(
    () => reloadSchema(formRef, true),
    [formRef]
  )

  const reloadFarmSelect = useCallback(
    () => {
      revalidateSchema()
      const farmsRef = formRef.current.getFieldRef('farms')
      farmsRef.clearError && farmsRef.clearError()
    },
    [formRef, revalidateSchema]
  )

  const handleModeChange = useCallback(
    ({ target: { value } }) => {
      setDisableFarms(old => {
        const newValue = value !== 'custom'

        if (old !== newValue) {
          reloadFarmSelect()
        }

        if (newValue) {
          formRef.current.setFieldValue('farms', [])
        }

        return newValue
      })
    },
    [formRef, reloadFarmSelect]
  )

  const handleSubmit = useCallback(
    (data) => {
      setLoading(true)

      dispatch(CompanyUserActions.saveCompanyUser(
        {
          isRootRequest: farmsEndpoint.includes('admin'),
          ...user,
          ...data,
          farms_always: data.farms_always === 'all',
          farms: map(data.farms, farmId => ({ id: farmId }))
        },
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: t('user', { howMany: 1 }),
            action: toLower(t(isEmpty(user) ? 'created' : 'updated', { gender: 'male', howMany: 1 }))
          }))
          onSuccess()
        },
        onError
      ))
    },
    [dispatch, farmsEndpoint, onError, onSuccess, setLoading, snackbar, t, user]
  )

  useEffect(
    () => {
      if (user?.id && !user.viewAllFarms) {
        reloadFarmSelect()
      }
    },
    [reloadFarmSelect, user]
  )

  useEffect(
    () => {
      if (!isEmpty(userFarmsDefaultValue)) {
        formRef.current.setFieldValue('farms', userFarmsDefaultValue)
      }
    },
    [formRef, setLoading, userFarmsDefaultValue]
  )

  useEffect(
    () => {
      if (!isEmpty(userData)) {
        const userHasFarms = !isEmpty(userData.farms)
        const mode = userHasFarms ? 'custom' : 'none'

        formRef.current.setData({
          company_role_id: userData.companyRoleId?.toString() ?? '',
          farms_always: userData.farmsAlways ? 'all' : mode
        })

        setLoading(false)
      }
    },
    [formRef, userData, isValidatingUserData, setLoading, user]
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => user.id && setLoading(true), [])

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ userSchema }
      onSubmit={ handleSubmit }
    >
      <Container>
        <Grid container spacing={ 2 }>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputText
              name="name"
              label={ t('name') }
              defaultValue={ user.name }
              disabled={ loading }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputPhone
              name="phone"
              label={ t('phone') }
              defaultValue={ user.phone || undefined }
              disabled={ loading }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputEmail
              name="email"
              label={ t('email') }
              defaultValue={ user.email }
              onChange={ revalidateSchema }
              disabled={ loading || !!user?.id }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputEmail
              name="email_confirmation"
              label={ t('confirm email') }
              defaultValue={ user.email }
              onChange={ revalidateSchema }
              disabled={ loading || !!user?.id }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputSelect
              name="company_role_id"
              label={ t('role', { howMany: 1 }) }
              options={ rolesOptions }
              disabled={ loading || isValidatingRoles || isValidatingUserData }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputText
              name="company_role_position"
              label={ t('role position', { howMany: 1 }) }
              defaultValue={ user.companyRolePosition }
              disabled={ loading || isValidatingUserData }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 }>
            <RadioGroup
              variant="row"
              name="farms_always"
              label={ t('farm accounts access') }
              defaultValue={ modeDefaultValue }
              options={ modeOptions }
              onChange={ handleModeChange }
              disabled={ loading }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } style={ { marginTop: 15 } }>
            <InputSelect
              name="farms"
              label={ t('farm', { howMany: 2 }) }
              defaultValue={ userFarmsDefaultValue }
              options={ farmOptions }
              disabled={ loading || disableFarms }
              loading={ isValidatingFarms }
              multiple
              fullWidth
            />
          </Grid>
        </Grid>
      </Container>
    </Form>
  )
})

UserForm.propTypes = {
  user: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  userDataEndpoint: PropTypes.string,
  farmsEndpoint: PropTypes.string.isRequired,
  rolesEndpoint: PropTypes.string.isRequired
}

UserForm.defaultProps = {
  user: {},
  userDataEndpoint: null,
  loading: false,
  setLoading: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default UserForm
